import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { VariableSizeList } from "react-window";

const ESTIMATED_ITEM_HEIGHT = 36;
const MAX_LIST_HEIGHT = 300;

interface RowProps {
   index: number;
   style: React.CSSProperties;
   data: {
      rows: React.ReactNode[];
      setRowHeight: (index: number, height: number) => void;
   };
}

const Row = ({ index, style, data }: RowProps) => {
   const ref = useRef<HTMLDivElement>(null);
   const { rows, setRowHeight } = data;

   useEffect(() => {
      if (ref.current) {
         setRowHeight(index, ref.current.getBoundingClientRect().height);
      }
   }, [index, setRowHeight]);

   return (
      <div style={{ ...style, height: "auto" }} ref={ref}>
         {rows[index]}
      </div>
   );
};

interface MenuListProps {
   children: React.ReactNode;
   maxHeight: number;
}

const VirtualizedMenuList: React.FC<MenuListProps> = ({ children, maxHeight }) => {
   const rows = React.Children.toArray(children);
   const listRef = useRef<VariableSizeList>(null);
   const rowHeightsRef = useRef<Record<number, number>>({});

   const getRowHeight = (index: number) => rowHeightsRef.current[index] ?? ESTIMATED_ITEM_HEIGHT;

   const setRowHeight = useCallback((index: number, height: number) => {
      if (rowHeightsRef.current[index] === height) return;
      rowHeightsRef.current[index] = height;
      listRef.current?.resetAfterIndex(index);
   }, []);

   const itemData = useMemo(() => ({ rows, setRowHeight }), [rows, setRowHeight]);

   const listHeight = Math.min(maxHeight || MAX_LIST_HEIGHT, rows.length * ESTIMATED_ITEM_HEIGHT);

   if (!rows.length) return null;

   return (
      <VariableSizeList
         ref={listRef}
         height={listHeight}
         itemCount={rows.length}
         itemSize={getRowHeight}
         width="100%"
         overscanCount={10}
         itemData={itemData}
      >
         {Row}
      </VariableSizeList>
   );
};

export default VirtualizedMenuList;
